/* ============================================================
 * <homepage-promo-popup>
 *   - Auto-opens after a configurable delay.
 *   - Frequency modes:
 *       once    -> shown a single time ever (localStorage),
 *                  i.e. new / first-time visitors only.
 *       session -> once per browser session (sessionStorage).
 *       always  -> every page load.
 *   - Closes on: X button, overlay click, Esc key.
 *   - Theme editor: opens when the section is selected so it
 *     can be customized without fighting the frequency rules.
 * ============================================================ */

if (!customElements.get('homepage-promo-popup')) {
  class HomepagePromoPopup extends HTMLElement {
    connectedCallback() {
      this.storageKey = this.dataset.storageKey || 'hpp-seen';
      this.showDelay = parseInt(this.dataset.showDelay, 10) || 0;
      this.frequency = this.dataset.frequency || 'once';

      this.bindEvents();

      if (window.Shopify && window.Shopify.designMode) {
        this.bindDesignMode();
        return;
      }

      if (this.hasBeenSeen()) return;

      this.openTimer = window.setTimeout(() => this.open(), this.showDelay);
    }

    disconnectedCallback() {
      if (this.openTimer) window.clearTimeout(this.openTimer);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    getStorage() {
      if (this.frequency === 'once') return window.localStorage;
      if (this.frequency === 'session') return window.sessionStorage;
      return null;
    }

    hasBeenSeen() {
      try {
        const storage = this.getStorage();
        return storage ? storage.getItem(this.storageKey) === '1' : false;
      } catch (e) {
        return false;
      }
    }

    markSeen() {
      try {
        const storage = this.getStorage();
        if (storage) storage.setItem(this.storageKey, '1');
      } catch (e) {
        /* storage disabled / private mode — silently ignore */
      }
    }

    bindEvents() {
      this.querySelectorAll('[data-hpp-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      const cta = this.querySelector('[data-hpp-cta]');
      if (cta) {
        cta.addEventListener('click', () => {
          // Let the navigation happen; just release the scroll lock.
          document.body.classList.remove('hpp-open');
        });
      }

      this.handleKeydown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.handleKeydown);
    }

    bindDesignMode() {
      const sectionId = this.dataset.sectionId;
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail && e.detail.sectionId === sectionId) this.open(true);
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail && e.detail.sectionId === sectionId) this.close(true);
      });
    }

    open(skipTracking) {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('hpp-open');
      // Mark as seen the moment it's shown so it truly only
      // ever triggers once for a new visitor.
      if (!skipTracking) this.markSeen();
      const closeBtn = this.querySelector('.hpp__close');
      if (closeBtn) {
        try {
          closeBtn.focus({ preventScroll: true });
        } catch (err) {
          /* older browsers */
        }
      }
    }

    close(skipTracking) {
      if (this.openTimer) window.clearTimeout(this.openTimer);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('hpp-open');
      if (!skipTracking) this.markSeen();
    }
  }

  customElements.define('homepage-promo-popup', HomepagePromoPopup);
}
